import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { submitOrder } from 'actions/app'
import { BackButton, Field, LoadingButton } from 'components'
import routes, { formatRoute } from 'config/routes'
import { Form, Formik } from 'formik'
import { AppLayout } from 'layouts'
import PropTypes from 'prop-types'
import React from 'react'
import {
  Alert,
  Card,
  Col,
  Container,
  Form as BsForm,
  ListGroup,
  Row,
} from 'react-bootstrap'
import { connect } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toPrice } from 'utils'
import * as Yup from 'yup'

const paymentMethodField = 'payment_method'

/**
 * Format the product title to include options
 */
const formatProductTitle = (title, allOptions, options) => {
  // debugger
  const formattedOptions = Object.keys(options)
    .sort()
    .map((optFieldCode) => {
      const optField = allOptions.find(
        (optField) => optField.code === optFieldCode
      )
      const optMap = optField.options.reduce((obj, o) => {
        obj[o.code] = o.text
        return obj
      }, {})
      return optMap[options[optFieldCode]]
    })
  return [title, ...formattedOptions].join(', ')
}

export function Review({
  appState,
  fundraiser,
  onFinish,
  products,
  submitOrder,
}) {
  const history = useHistory()
  const { shortcode } = useParams()
  const { cart } = appState
  const currency = React.useMemo(
    () =>
      cart.reduce(
        (currency, product) =>
          products.find((p) => p.id === product.id).currency,
        'usd'
      ),
    [cart, products]
  )
  const [paymentEvent, setPaymentEvent] = React.useState({})
  const [paymentError, setPaymentError] = React.useState()
  const [editingPayment, setEditingPayment] = React.useState(false)
  const [includeShipping, setIncludeShipping] = React.useState(false)
  const stripe = useStripe()
  const elements = useElements()

  const productMap = React.useMemo(
    () =>
      products.reduce((obj, p) => {
        obj[p.id] = p
        return obj
      }, {}),
    [products]
  )

  const requireShippingInfo = React.useMemo(
    () =>
      cart.reduce((require, i) => require || products.find((p) => p.id === i.id)?.require_shipping_info, false),
    [cart, products]
  )

  const initialValues = {
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_phone: '',
    customer_street_address_1: '',
    customer_street_address_2: '',
    customer_city: '',
    customer_district: '',
    customer_postal_code: '',
    flat_shipping: false,
    payment_method: false,
  }

  const validationSchema = requireShippingInfo
    ? Yup.object().shape({
        customer_first_name: Yup.string().required('Required'),
        customer_last_name: Yup.string().required('Required'),
        customer_email: Yup.string()
          .email('Please enter a valid email address')
          .required('Required'),
        customer_phone: Yup.string().required('Required'),
        customer_street_address_1: Yup.string().required('Required'),
        customer_street_address_2: Yup.string(),
        customer_city: Yup.string().required('Required'),
        customer_district: Yup.string().required('Required'),
        customer_postal_code: Yup.string().required('Required'),
      })
    : Yup.object().shape({
        customer_first_name: Yup.string().required('Required'),
        customer_last_name: Yup.string().required('Required'),
        customer_email: Yup.string()
          .email('Please enter a valid email address')
          .required('Required'),
      })

  const validate = (values) => {
    const errors = {}
    if (paymentEvent.error?.message) {
      errors[paymentMethodField] = paymentEvent.error.message
    } else if (!values.payment_method && !editingPayment) {
      errors[paymentMethodField] = 'Required'
    }
    return errors
  }

  const handlePaymentMethodChange = (event, formik) => {
    setPaymentEvent(event)
    formik.setFieldValue(paymentMethodField, event.complete)
    formik.setFieldTouched(paymentMethodField, true)
  }

  const submit = async (values, formik) => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return
    }

    setPaymentError(null)
    const cardElement = elements.getElement(CardElement)
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })

    if (error) {
      console.error('[payment error]', error)
      setPaymentError(error.message)
      return
    }

    try {
      const order = await submitOrder({
        ...appState,
        ...values,
        payment_method: paymentMethod.id,
      })
      onFinish(order)
    } catch (err) {
      console.error('error submitting order')
      console.error(err)
      setPaymentError(
        err.response?.status === 402 &&
          err.response?.data?.type === 'card_error'
          ? err.response?.data?.message
          : 'An error occurred while processing your order'
      )
    }
  }

  const { totalPrice, totalProcessingFee, totalSalesTax } = cart.reduce(
    (data, { id, quantity }) => {
      const { price, processing_fee, sales_tax } = products.find(
        (p) => p.id === id
      )
      return {
        totalPrice: data.totalPrice + price * quantity,
        totalProcessingFee: data.totalProcessingFee + processing_fee * quantity,
        totalSalesTax: data.totalSalesTax + sales_tax * quantity,
      }
    },
    {
      totalPrice: includeShipping ? fundraiser.flat_shipping : 0,
      totalProcessingFee: includeShipping ? (fundraiser.flat_shipping * 0.029) : 0,
      totalSalesTax: 0,
    }
  )

  const handleFlatShipping = (event, setFieldValue) => {
    const shipping = event.target.checked && event.target.id === 'shipping'
    setIncludeShipping(shipping)
    setFieldValue('flat_shipping', shipping)
  }

  return (
    <AppLayout>
      <BackButton
        onClick={() =>
          history.push(formatRoute(routes.selectReward, { shortcode }))
        }
      />
      <Container>
        <Row>
          <Col md={6}>
            <h1>Review</h1>
            <p className="lead mb-5">
              Please review your purchase details below.
            </p>
          </Col>
        </Row>
        <Row className="mb-4 mb-lg-0">
          <Col md={12} lg={4} className="mb-4 mb-lg-0">
            <ListGroup className="mb-3">
              <ListGroup.Item>
                <h2 className="h5 m-0">
                  <i className="fas fa-shopping-cart fa-fw mr-1" />
                  Cart
                </h2>
              </ListGroup.Item>
              {cart.map((item) => (
                <ListGroup.Item key={item.fullId}>
                  <h6 className="text-transform-none">
                    {formatProductTitle(
                      productMap[item.id].title,
                      productMap[item.id].options,
                      item.options
                    )}
                  </h6>
                  <Row>
                    <Col xs={6}>
                      <BsForm.Group>
                        <BsForm.Label className="small text-muted">
                          Quantity
                        </BsForm.Label>
                        <p className="my-1 heavyset">{item.quantity}</p>
                      </BsForm.Group>
                    </Col>
                    <Col xs={6} className="text-right">
                      <BsForm.Label className="small text-muted">
                        Total
                      </BsForm.Label>
                      <p className="my-1 text-success heavyset">
                        ${toPrice(productMap[item.id].price * item.quantity)}
                      </p>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
              {totalSalesTax > 0 && totalProcessingFee > 0 ? (
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <p className="h6 m-0 text-transform-none">
                    Taxes and Processing Fees
                  </p>
                  <p className="m-0 text-success heavyset">
                    ${toPrice(totalSalesTax + totalProcessingFee)}
                  </p>
                </ListGroup.Item>
              ) : totalSalesTax > 0 ? (
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <p className="h6 m-0 text-transform-none">Sales Tax</p>
                  <p className="m-0 text-success heavyset">
                    ${toPrice(totalSalesTax)}
                  </p>
                </ListGroup.Item>
              ) : totalProcessingFee > 0 ? (
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <p className="h6 m-0 text-transform-none">Processing</p>
                  <p className="m-0 text-success heavyset">
                    ${toPrice(totalProcessingFee)}
                  </p>
                </ListGroup.Item>
              ) : null}
              {includeShipping && (
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <p className="h6 m-0 text-transform-none">Shipping</p>
                  <p className="m-0 text-success heavyset">
                    ${toPrice(fundraiser.flat_shipping)}
                  </p>
                </ListGroup.Item>
              )}
              <ListGroup.Item className="d-flex justify-content-between align-items-center">
                <p className="h5 m-0">Total</p>
                <p className="h5 m-0 text-success heavyset">
                  ${toPrice(totalPrice + totalSalesTax + totalProcessingFee)}
                  <span className="text-secondary font-weight-normal small pl-1">
                    {currency}
                  </span>
                </p>
              </ListGroup.Item>
            </ListGroup>
          </Col>
          <Col lg={1} className="d-none d-lg-block"></Col>
          <Col>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              validate={validate}
              onSubmit={submit}
            >
              {(formik) => (
                <Form>
                  {fundraiser.flat_shipping > 0 && (
                    <Card className="mb-3">
                      <Card.Body>
                        <Card.Title>Shipping</Card.Title>
                        <Card.Text>
                          Have items shipped directly to your house for a flat
                          shipping fee.
                        </Card.Text>
                        <BsForm.Check
                          type="radio"
                          id="shipping"
                          label={`Yes, add $${toPrice(
                            fundraiser.flat_shipping
                          )} and ship my order direct to my house`}
                          checked={formik.values.flat_shipping}
                          onChange={(event) => handleFlatShipping(event, formik.setFieldValue)}
                        />
                        <BsForm.Check
                          type="radio"
                          id="pickup"
                          label={`No, I'll pick up my order from my host`}
                          checked={!formik.values.flat_shipping}
                          onChange={(event) => handleFlatShipping(event, formik.setFieldValue)}
                        />
                      </Card.Body>
                    </Card>
                  )}
                  <Card>
                    <Card.Body>
                      <Card.Title>Payment Info</Card.Title>
                      <Card.Text>Please enter your payment info</Card.Text>
                      {paymentError && (
                        <Alert variant="danger">{paymentError}</Alert>
                      )}
                      <Field name="customer_first_name" label="First Name" />
                      <Field name="customer_last_name" label="Last Name" />
                      <Field
                        name="customer_email"
                        label="Email"
                        help={`We'll send your receipt to this address as well as details about your rewards.`}
                      />
                      {requireShippingInfo && (
                        <>
                          <Field name="customer_phone" label="Phone" />
                          <Field
                            name="customer_street_address_1"
                            label="Street Address"
                          />
                          <Field
                            name="customer_street_address_2"
                            label="Street Address 2 (optional)"
                          />
                          <BsForm.Row>
                            <Col xs="6">
                              <Field name="customer_city" label="City" />
                            </Col>
                            <Col xs="3">
                              <Field name="customer_district" label="State" />
                            </Col>
                            <Col xs="3">
                              <Field
                                name="customer_postal_code"
                                label="Zip Code"
                              />
                            </Col>
                          </BsForm.Row>
                        </>
                      )}
                      <BsForm.Group>
                        <BsForm.Label>Credit or Debit Card</BsForm.Label>
                        <BsForm.Control
                          name="credit_card"
                          className="pt-2"
                          as={CardElement}
                          onChange={(event) =>
                            handlePaymentMethodChange(event, formik)
                          }
                          onFocus={() => setEditingPayment(true)}
                          onBlur={() => setEditingPayment(false)}
                          options={{
                            style: {
                              base: {
                                fontSize: '16px',
                              },
                            },
                          }}
                        />
                        {formik.errors.payment_method &&
                          formik.touched.payment_method && (
                            <BsForm.Text className="small text-danger">
                              {formik.errors.payment_method}
                            </BsForm.Text>
                          )}
                      </BsForm.Group>
                      <Row className="justify-content-end">
                        <Col className="col-auto">
                          <p className="small text-muted">
                            Payments processed by{' '}
                            <a
                              href="https://stripe.com"
                              target="_blank"
                              rel="noopener noreferrer"
                              tabIndex="-1"
                            >
                              <i className="fab fa-stripe fa-3x stripe-logo" />
                            </a>
                          </p>
                        </Col>
                      </Row>
                      <Row className="justify-content-end">
                        <Col md={4}>
                          <LoadingButton
                            loading={formik.isSubmitting}
                            variant="primary"
                            type="submit"
                            block
                          >
                            {formik.isSubmitting ? 'Processing' : 'Pay'}
                          </LoadingButton>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </AppLayout>
  )
}

Review.propTypes = {
  appState: PropTypes.object.isRequired,
  fundraiser: PropTypes.object.isRequired,
  onFinish: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  submitOrder: PropTypes.func.isRequired,
}

const mapState = (state) => ({
  appState: state.app,
  fundraiser: state.fundraiser,
  products: state.products,
})

const mapDispatch = {
  submitOrder,
}

export default connect(mapState, mapDispatch)(Review)
